import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../redux";
import { View, Text, Button, Badge } from "../../components/base";
import { Icon } from "../../components/base/Icon";
import { PageLayout } from "../../components/PageLayout";
import { evaluatorActions } from "../../redux/reducers/evaluatorSlice";
import { useEffectiveOrganization } from "../../hooks/useEffectiveOrganization";
import { useAuth } from "../../hooks/useAuth";

export const EvaluatorsPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { organization } = useEffectiveOrganization();

  const evaluatorEntities = useAppSelector((state) => state.evaluator.entities);
  const evaluatorsIsLoading = useAppSelector((state) => state.evaluator.isLoading);

  const evaluators = useMemo(() => {
    if (evaluatorEntities && organization) {
      return Object.values(evaluatorEntities).filter((evaluator) => evaluator.organizationId === organization.id);
    }
    return [];
  }, [evaluatorEntities, organization]);

  useEffect(() => {
    if (organization) {
      dispatch(evaluatorActions.get({}));
    }
  }, [dispatch, organization]);

  return (
    <PageLayout
      title="Evaluators"
      description="Create evaluators to score or extract data from your agent sessions."
    >
      <View className="flex flex-col gap-4 mt-8">
        <View className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <Text className="text-lg font-semibold">All Evaluators</Text>
          <Button
            variant="brand"
            onClick={() => navigate("/evaluators/new")}
            isDisabled={evaluatorsIsLoading}
          >
            <Icon
              name="add"
              size={18}
              className="mr-2"
            />
            New Evaluator
          </Button>
        </View>

        {evaluatorsIsLoading ? (
          <View className="flex items-center justify-center py-8">
            <Text className="text-sm text-gray-500 dark:text-gray-400">Loading evaluators...</Text>
          </View>
        ) : evaluators.length === 0 ? (
          <View className="flex flex-col items-center justify-center gap-2 py-8 rounded-lg border border-dashed border-gray-300 dark:border-gray-700">
            <Icon
              name="checklist"
              size={32}
              className="text-indigo-500"
            />
            <Text className="text-sm text-gray-500 dark:text-gray-400 text-center max-w-md">
              Create an evaluator to score or extract data from your agent sessions.
            </Text>
          </View>
        ) : (
          <View className="flex flex-col gap-3">
            {evaluators.map((evaluator) => (
              <View
                key={evaluator.id}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/evaluators/${evaluator.id}/details`)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    navigate(`/evaluators/${evaluator.id}/details`);
                  }
                }}
                className="flex flex-col gap-3 border border-gray-200 dark:border-gray-800 rounded-lg p-4 bg-white dark:bg-gray-900 cursor-pointer transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-brand"
              >
                <View className="flex flex-col gap-1 w-full">
                  <View className="flex flex-row justify-between">
                    <Text className="text-base font-semibold text-gray-900 dark:text-white">{evaluator.title}</Text>
                    <View className="flex flex-row gap-2">
                      <Badge
                        title="Eval Type"
                        variant="brand"
                        className="capitalize cursor-default pointer-events-none"
                      >
                        {evaluator.evalType}
                      </Badge>
                      <Badge
                        title="Target Format"
                        variant="info"
                        className="capitalize cursor-default pointer-events-none"
                      >
                        {evaluator.targetFormat}
                      </Badge>
                    </View>
                  </View>
                </View>
              </View>
            ))}
          </View>
        )}
      </View>
    </PageLayout>
  );
};
